const STORAGE_PREFIX = 'volksign_recipient_contacts_org_';
const ACTIVE_ORG_SESSION_KEY = 'volksign_active_organization_id';
/** @deprecated Unscoped — removed on logout / org change (cross-tenant leak risk). */
const LEGACY_STORAGE_KEY = 'volksign_recipient_contacts_v1';

export const CONTACT_SEARCH_MIN_CHARS = 3;
export const CONTACT_SEARCH_MIN_NAME_WORDS = 3;

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

export function normalizeContactRow(raw) {
  if (!raw) return null;
  const email = String(raw.email || '').trim();
  const name = String(raw.name || '').trim();
  if (!email) return null;
  return {
    id: raw.id ?? null,
    email,
    name,
    recipient_type: raw.recipient_type || raw.type || null,
    signee_color: raw.signee_color || raw.color || null,
    updated_at: raw.updated_at || null,
  };
}

function collectContactArrays(payload) {
  if (!payload || typeof payload !== 'object') return [];
  const candidates = [
    payload,
    payload.contacts,
    payload.contact_list,
    payload.contactList,
    payload.data,
    payload.data?.contacts,
    payload.data?.contact_list,
    payload.data?.contactList,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  return [];
}

export function normalizeOrganizationId(value) {
  if (value === undefined || value === null || value === '') return null;
  return String(value);
}

export function getActiveOrganizationId() {
  if (typeof window === 'undefined') return null;
  try {
    return normalizeOrganizationId(sessionStorage.getItem(ACTIVE_ORG_SESSION_KEY));
  } catch {
    return null;
  }
}

export function setActiveOrganizationId(organizationId) {
  if (typeof window === 'undefined') return;
  const next = normalizeOrganizationId(organizationId);
  try {
    if (!next) {
      sessionStorage.removeItem(ACTIVE_ORG_SESSION_KEY);
      return;
    }
    const prev = getActiveOrganizationId();
    sessionStorage.setItem(ACTIVE_ORG_SESSION_KEY, next);
    if (prev && prev !== next) {
      clearAllRecipientContactsStorage({ keepActiveOrg: true });
    }
  } catch {
    /* ignore */
  }
}

function storageKeyForOrg(organizationId) {
  const orgId = normalizeOrganizationId(organizationId);
  if (!orgId) return null;
  return `${STORAGE_PREFIX}${orgId}`;
}

/** Remove all cached contacts (required on logout and org switch). */
export function clearAllRecipientContactsStorage(options = {}) {
  if (typeof window === 'undefined') return;
  const { keepActiveOrg = false } = options;
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    if (!keepActiveOrg) {
      sessionStorage.removeItem(ACTIVE_ORG_SESSION_KEY);
    }
  } catch {
    /* ignore */
  }
}

/** True when contact-list API indicates failure (status/success fields). */
export function isContactListApiFailure(response) {
  if (!response || typeof response !== 'object') return true;
  if (response.success === true) return false;
  if (response.success === false) return true;
  const status = String(response.status || '').toLowerCase();
  if (status === 'success') return false;
  return status === 'error' || status === 'failed';
}

/**
 * GET /api/contact-list — expected shape:
 * { success: true, data: { scope, organization_id, contacts: [], total } }
 */
export function extractOrganizationIdFromContactListResponse(response) {
  if (!response || typeof response !== 'object') return null;
  if (response.success === true && response.data && typeof response.data === 'object') {
    return normalizeOrganizationId(response.data.organization_id);
  }
  return normalizeOrganizationId(response.organization_id);
}

export function extractContactsFromApiResponse(response) {
  if (!response || typeof response !== 'object') return [];

  if (response.success === true && response.data && typeof response.data === 'object') {
    const list = response.data.contacts;
    if (Array.isArray(list)) {
      return list.map(normalizeContactRow).filter(Boolean);
    }
  }

  const payload = response?.data ?? response;
  const rawList = collectContactArrays(payload);
  return rawList.map(normalizeContactRow).filter(Boolean);
}

export function loadStoredRecipientContacts(organizationId) {
  if (typeof window === 'undefined') return [];
  const orgId = normalizeOrganizationId(organizationId) ?? getActiveOrganizationId();
  if (!orgId) return [];

  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* ignore */
  }

  const storageKey = storageKeyForOrg(orgId);
  if (!storageKey) return [];

  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeContactRow).filter(Boolean);
  } catch {
    return [];
  }
}

export function saveStoredRecipientContacts(contacts, organizationId) {
  if (typeof window === 'undefined') return [];
  const orgId = normalizeOrganizationId(organizationId) ?? getActiveOrganizationId();
  if (!orgId) return [];

  setActiveOrganizationId(orgId);

  const list = (Array.isArray(contacts) ? contacts : [])
    .map(normalizeContactRow)
    .filter(Boolean);
  const byEmail = new Map();
  list.forEach((row) => {
    const key = normalizeEmail(row.email);
    if (!key) return;
    byEmail.set(key, { ...byEmail.get(key), ...row, email: row.email });
  });
  const normalized = Array.from(byEmail.values());

  try {
    const storageKey = storageKeyForOrg(orgId);
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(normalized));
    }
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* ignore */
  }

  return normalized;
}

/** Merge rows into the org-scoped store only (never across organizations). */
export function mergeContactsIntoStore(incoming, organizationId) {
  const orgId = normalizeOrganizationId(organizationId) ?? getActiveOrganizationId();
  if (!orgId) return [];

  const existing = loadStoredRecipientContacts(orgId);
  const byEmail = new Map();
  [...existing, ...(Array.isArray(incoming) ? incoming : [])]
    .map(normalizeContactRow)
    .filter(Boolean)
    .forEach((row) => {
      const key = normalizeEmail(row.email);
      if (!key) return;
      byEmail.set(key, { ...byEmail.get(key), ...row });
    });
  return saveStoredRecipientContacts(Array.from(byEmail.values()), orgId);
}

export function countNameSearchWords(value) {
  return String(value || '')
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0).length;
}

/** Show suggestions after 3+ chars (email) or 3+ name words / 3+ chars (name). */
export function shouldShowContactSuggestions(field, query) {
  const trimmed = String(query || '').trim();
  if (!trimmed) return false;
  if (field === 'email') {
    return trimmed.length >= CONTACT_SEARCH_MIN_CHARS;
  }
  return (
    trimmed.length >= CONTACT_SEARCH_MIN_CHARS
    || countNameSearchWords(trimmed) >= CONTACT_SEARCH_MIN_NAME_WORDS
  );
}

export function filterRecipientContacts(contacts, query, field) {
  if (!shouldShowContactSuggestions(field, query)) return [];
  const q = String(query || '').trim().toLowerCase();
  if (!q) return [];

  const tokens = q.split(/\s+/).filter(Boolean);

  return (Array.isArray(contacts) ? contacts : [])
    .filter((contact) => {
      const name = String(contact.name || '').toLowerCase();
      const email = String(contact.email || '').toLowerCase();
      if (field === 'name') {
        return tokens.every((token) => name.includes(token));
      }
      if (field === 'email') {
        return email.includes(q);
      }
      return name.includes(q) || email.includes(q);
    })
    .slice(0, 10);
}
